import TextUs, { useSmsMessage } from "@/components/TextUs";
import { WhatsAppIcon } from "@/components/ContactButtons";
import BrandLine from "@/components/BrandLine";
import { whatsappHref } from "@/lib/contact";
import { useRouterState } from "@tanstack/react-router";
import { campaignPage } from "@/lib/campaign-pages";
import type { ReactNode } from "react";
import {
  ArrowUpFromLine, Wine, Tv, Speaker, Smartphone, Lightbulb, Armchair, Wifi,
  ShieldCheck, Sparkles, GlassWater, Star,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import BigDates from "@/components/BigDates";
import TextMariaSection from "@/components/TextMariaSection";
import FamilyChauffeurs from "@/components/FamilyChauffeurs";
import BigDatesAlertsSignup from "@/components/BigDatesAlertsSignup";

export const SITE = "https://atlantaluxurysprinters.com";

export type Faq = { q: string; a: string };
export type Vehicle = { name: string; cap: string; detail: string; img: string; alt: string };

const stripConfirm = (s: string) => s.replace(/\s*\[CONFIRM[^\]]*\]/g, "");

export function buildGameDaySchemas(opts: {
  url: string; name: string; description: string; serviceType: string;
  areas: string[]; faqs: Faq[]; crumb: string;
}) {
  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
          { "@type": "ListItem", position: 2, name: opts.crumb, item: opts.url },
        ],
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: opts.name,
        serviceType: opts.serviceType,
        url: opts.url,
        description: opts.description,
        areaServed: opts.areas.map((a) => ({
          "@type": "City", name: a, containedInPlace: { "@type": "State", name: "GA" },
        })),
        provider: {
          "@type": "LocalBusiness",
          name: "Atlanta Luxury Sprinters",
          url: SITE,
          telephone: "+1-678-431-7222",
          address: {
            "@type": "PostalAddress",
            streetAddress: "3401 Norman Berry Dr Suite 146",
            addressLocality: "Atlanta",
            addressRegion: "GA",
            postalCode: "30344",
            addressCountry: "US",
          },
        },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: opts.faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: stripConfirm(f.a) },
        })),
      }),
    },
  ];
}

const FEATURES = [
  { icon: ArrowUpFromLine, t: "Stand-up headroom", d: "Walk around and stand up inside every Sprinter." },
  { icon: Wine, t: "Built-in bar", d: "Glassware, ice and a lit bar top." },
  { icon: Tv, t: "2 flat-screen TVs", d: "Built into the walls, so you can watch the pre-game on the way." },
  { icon: Speaker, t: "Premium sound system", d: "Club-quality speakers on both sides." },
  { icon: Smartphone, t: "Bluetooth & Apple CarPlay", d: "Play your own playlist from your phone." },
  { icon: Lightbulb, t: "LED party lighting", d: "Color-changing ceiling and accent lights." },
  { icon: Armchair, t: "Leather lounge seating", d: "Comfortable seating for up to 14." },
  { icon: Wifi, t: "WiFi & USB charging", d: "Keep everyone's phones charged for game day photos." },
];

const REVIEWS = [
  { name: "Angela Durrence", text: "Kevin was on time and very professional! He took us to the Ameris Bank Amphitheatre to a concert. When we got out of the concert he was right there waiting. No waiting on an Uber ...this driver had us back at our hotel within 15 minutes." },
  { name: "Brandon Parker", text: "Maria and her team are punctual and professional. One of the best car services I've ever used when traveling. The fleet of cars are extremely clean and sanitized. Her and her team are always willing to help or accommodate whenever possible." },
  { name: "Ralph Cerny", text: "Each time we are amazed with the service. Every driver is extremely professional and engaging. Kevin was our driver, he was fantastic. ... Outstanding service." },
];

export const PRICING_LINE =
  "Sprinters from $250/hour · Custom quotes given upon request · 50% deposit secures your date · Your final quote is always in writing before you book.";

export function Section({ title, intro, children, alt }: { title: string; intro?: ReactNode; children?: ReactNode; alt?: boolean }) {
  return (
    <section className={`section-padding ${alt ? "bg-surface" : ""}`}>
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-4">{title}</h2>
        {intro && <div className="text-muted-foreground text-center max-w-3xl mx-auto mb-10 space-y-3">{intro}</div>}
        {children}
      </div>
    </section>
  );
}

export default function GameDayPage(props: {
  eyebrow: string;
  h1: string;
  intro: string;
  heroImg: string;
  heroAlt: string;
  champagneText: string;
  smallPrint: string;
  bigDates: { heading: string; events: string[]; note?: string };
  steps: { title: string; items: { t: string; d: string }[]; note?: string };
  middle?: ReactNode;
  vehicles: { title: string; intro?: string; list: Vehicle[]; note?: string; showPricing?: boolean };
  afterVehicles?: ReactNode;
  faqs: Faq[];
  faqTitle: string;
  occasion: string;
  bookTitle: string;
  differentiation?: { title: string; intro?: string; customBuiltText?: string };
}) {
  const diff = props.differentiation;
  const heroLabel = campaignPage(useRouterState({ select: (s) => s.location.pathname }))?.label ?? "Text Maria";
  const smsMsg = useSmsMessage();
  const trust = [
    { icon: ShieldCheck, t: "Safety first", d: "Background-checked chauffeurs certified by the Georgia State Commissioner and trained to executive protocol. We invest in our vehicles, safety and technology, because your safety is our priority." },
    { icon: Sparkles, t: diff ? "Brand-new, custom-built Sprinters" : "Brand-new, custom-built", d: diff?.customBuiltText ?? "No tired, beat-up buses. Every Sprinter is one of one." },
    { icon: GlassWater, t: "BYOB bar", d: props.champagneText },
  ];
  return (
    <>
      <Header />
      <main>
        <section data-track-location="hero" className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-24 pb-10">
          <div className="absolute inset-0">
            <img src={props.heroImg} alt={props.heroAlt} width={1400} height={933} className="w-full h-full object-cover" fetchPriority="high" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
            <p className="text-purple-light text-xs font-medium tracking-[0.4em] uppercase mb-5">{props.eyebrow}</p>
            <h1 className="font-heading text-3xl sm:text-5xl font-semibold leading-[1.1] mb-5 text-white">{props.h1}</h1>
            <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-8">{props.intro}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <TextUs className="btn-primary-hero rounded-sm inline-flex items-center justify-center gap-2" desktopClassName="btn-primary-hero rounded-sm" ariaLabel={`${heroLabel} by text`} primary>{heroLabel}</TextUs>
              <a href={whatsappHref(smsMsg)} target="_blank" rel="noopener noreferrer" className="btn-outline-hero rounded-sm inline-flex items-center justify-center gap-2">
                <WhatsAppIcon /> WhatsApp
              </a>
            </div>
            <BrandLine />
          </div>
        </section>

        <section className="py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-3">{diff ? diff.title : "The Party Bus Experience. The Luxury of a Limo."}</h2>
            {(!diff || diff.intro) && (
              <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8">
                {diff ? diff.intro : "One of Atlanta's newest custom-built Sprinter limo fleets: comfortable, safe and built for game day."}
              </p>
            )}
            {!diff && <ul className="grid gap-4 grid-cols-2 lg:grid-cols-4">
              {FEATURES.map((f) => (
                <li key={f.t} className="rounded-md border border-border bg-card p-5">
                  <f.icon className="w-6 h-6 text-primary mb-2" aria-hidden="true" />
                  <h3 className="font-heading text-base mb-1">{f.t}</h3>
                  <p className="text-muted-foreground text-sm">{f.d}</p>
                </li>
              ))}
            </ul>}
            <div className={`grid gap-4 md:grid-cols-3 ${diff ? "mt-8" : "mt-6"}`}>
              {trust.map((p) => (
                <div key={p.t} className="rounded-md border border-primary/30 bg-primary/5 p-5">
                  <p.icon className="w-6 h-6 text-primary mb-2" aria-hidden="true" />
                  <h3 className="font-heading text-base mb-1">{p.t}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-muted-foreground mt-5">{props.smallPrint}</p>
          </div>
        </section>

        <BigDates events={props.bigDates.events} showGameDayLink={false} heading={props.bigDates.heading} note={props.bigDates.note} />

        <Section title={props.steps.title}>
          <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {props.steps.items.map((s, i) => (
              <li key={s.t} className="rounded-md border border-border bg-card p-6">
                <span className="text-primary font-heading text-3xl">{i + 1}</span>
                <h3 className="mt-2 font-heading text-lg">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </li>
            ))}
          </ol>
          {props.steps.note && <p className="text-center text-sm text-muted-foreground mt-6">{props.steps.note}</p>}
        </Section>

        {props.middle}

        <Section title={props.vehicles.title} intro={props.vehicles.intro && <p>{props.vehicles.intro}</p>} alt>
          <div className={`grid gap-6 sm:grid-cols-2 ${props.vehicles.list.length > 3 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
            {props.vehicles.list.map((v) => (
              <figure key={v.name} className="rounded-md border border-border bg-card overflow-hidden">
                <img src={v.img} alt={v.alt} loading="lazy" decoding="async" width={1000} height={667} className="w-full aspect-[3/2] object-cover" />
                <figcaption className="p-5">
                  <h3 className="font-heading text-lg">{v.name}</h3>
                  <p className="text-primary text-sm font-medium">{v.cap}</p>
                  <p className="text-muted-foreground text-sm mt-1">{v.detail}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          {props.vehicles.note && <p className="text-center text-sm text-muted-foreground mt-6">{props.vehicles.note}</p>}
          {props.vehicles.showPricing && <p className="text-center font-medium text-foreground mt-6">{PRICING_LINE}</p>}
        </Section>

        {props.afterVehicles}

        <Section title="What Our Riders Say">
          <div className="grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r) => (
              <figure key={r.name} className="rounded-md border border-border bg-card p-6">
                <div className="flex gap-1 mb-3" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" />)}
                </div>
                <blockquote className="text-muted-foreground italic leading-relaxed">"{r.text}"</blockquote>
                <figcaption className="mt-4 text-sm"><span className="font-medium text-foreground">{r.name}</span>, Google review</figcaption>
              </figure>
            ))}
          </div>
        </Section>

        <section className="section-padding bg-surface">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-center mb-8">{props.faqTitle}</h2>
            <div className="space-y-6">
              {props.faqs.map((f) => (
                <div key={f.q}>
                  <h3 className="font-heading text-lg mb-1">{f.q}</h3>
                  <p className="text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FamilyChauffeurs compact />
        <TextMariaSection
          title={props.bookTitle}
          body="Text me your date, pickup address and group size. I'll check which Sprinter is open and send your quote in writing. It's the fastest way to check your game day."
        />

        <BigDatesAlertsSignup />
      </main>
      <Footer />
      <FloatingButtons />
    </>
  );
}
